import { ChangeEvent, ReactElement, useState } from 'react';
import { createNotification, uploadFile, uuid } from '@/utils';

import ImageInput from '@/common/ImageInput';
import { LoadingBlur } from '@/components/LoadingSpinner';
import { MAX_PROFILE_IMAGE_SIZE_MB } from '@/constants';
import { Toast } from '@/models';
import { UserModalTypes } from '@/pages/Users/components/UserModal/types';
import generateUploadProfileImageUrl from '@/api/services/users/generateUploadProfileImageUrl';
import updateUserBulk from '@/pages/Users/api/services/updateUserBulk';

const title = 'Upload Profile Image';

const withCacheBust = (url?: string): string | undefined => (url ? `${url.split('?')[0]}?key=${uuid()}` : undefined);

const UserAvatar = ({ userId, src }: UserModalTypes.AvatarProps): ReactElement => {
  const [image, setImage] = useState<string | undefined>(() => withCacheBust(src));
  const [uploading, setUploading] = useState<boolean>(false);

  const handleUploadFile = async (e: ChangeEvent<HTMLInputElement>): Promise<void> => {
    const file = e.target.files?.[0];
    if (!file || !userId) return;

    const megabytes = file.size / (1024 * 1024);
    if (megabytes > MAX_PROFILE_IMAGE_SIZE_MB) {
      createNotification(
        `Image is too large (${megabytes.toFixed(2)} MB). Maximum size is ${MAX_PROFILE_IMAGE_SIZE_MB} MB.`,
        Toast.Type.WARNING,
        title
      );
      return;
    }

    try {
      setUploading(true);

      const { contentType, url } = (await generateUploadProfileImageUrl(userId, file.name)) || {};
      if (!contentType || !url) throw new Error('Could not generate an upload URL.');

      const uploadResponse = await uploadFile({ file, url, contentType });
      const uploadedUrl = uploadResponse?.file?.url;
      if (!uploadedUrl) throw new Error('Failed to upload image.');

      const avatar = uploadedUrl.split('?')[0];
      const updateResponse = await updateUserBulk([userId], { avatar }, { avatar: src });
      const errors = (updateResponse?.runUpdateUserProfileBulk?.response || [])
        .filter((output): boolean => !!output?.error)
        .map((output): string => `${output?.error?.code} ${output?.error?.message}`)
        .join('\n');
      if (errors) throw new Error(errors);

      setImage(withCacheBust(avatar));
      createNotification('Image uploaded successfully.', Toast.Type.SUCCESS, title);
    } catch (err) {
      console.error(err.message || err);
      createNotification(err.message || 'Failed to upload image.', Toast.Type.DANGER, title);
      setImage(withCacheBust(src));
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="UserModal-Avatar">
      <LoadingBlur loading={uploading} />
      <ImageInput src={image} disabled={!userId} onChange={handleUploadFile}>
        <i className="sv sv-user" />
      </ImageInput>
    </div>
  );
};

export default UserAvatar;
